import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import {
  ConsultationStatus,
  DocumentStatus,
} from '@/shared/api/transcriptor/transcriptorApi';
import { Button, Spinner } from '@/shared/ui';

import { isNoSpeechDetectedError } from '@/entities/transcriptor';

import { dentalConsultationMessages } from '../../config';
import {
  isConsultationProcessing,
  isGeneratingDocumentStatus,
} from '../../lib';

import styles from './ConsultationEmptyState.module.scss';

type ConsultationEmptyStateProps = {
  className?: string;
  status?: ConsultationStatus;
  documentStatus?: DocumentStatus;
  documentError?: string | null;
  noDataMessage: MessageDescriptor;
  onRegenerate?: () => void;
};

export const ConsultationEmptyState: FC<ConsultationEmptyStateProps> = ({
  className,
  status,
  documentStatus,
  documentError,
  noDataMessage,
  onRegenerate,
}) => {
  const { formatMessage } = useIntl();
  const isRecording = status === 'recording';
  const isProcessing = !isRecording && isConsultationProcessing(status);
  const isFailed = status === 'failed';
  const isSkipped = documentStatus === 'skipped';
  const isDocumentFailed = !isFailed && documentStatus === 'failed';
  // A failed consultation is terminal: don't show the in-progress spinner for a
  // document still stuck in pending/generating, otherwise the failed state with
  // its Regenerate action would never be reachable.
  const isGenerating = !isFailed && isGeneratingDocumentStatus(documentStatus);

  if (isSkipped) {
    return (
      <div className={cn(styles.container, 'p2', className)}>
        <div className={styles.skippedState}>
          {isNoSpeechDetectedError(documentError) ? (
            <FormattedMessage
              {...dentalConsultationMessages.noSpeechDetectedLabel}
            />
          ) : (
            <FormattedMessage {...dentalConsultationMessages.reportSkipped} />
          )}
        </div>
      </div>
    );
  }

  // No Regenerate action for a single failed tab: reprocessing re-runs every
  // tab and consumes a new report, and the rest of this report is fine. A
  // consultation that failed as a whole is different — its charge was already
  // refunded — and keeps the action in the branch below.
  if (isDocumentFailed) {
    return (
      <div className={cn(styles.container, 'p2', className)}>
        <div className={styles.failedState}>
          <FormattedMessage
            id="dentalConsultation.status.documentFailed"
            defaultMessage="Failed to generate this document"
          />
        </div>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className={cn(styles.container, 'p2', className)}>
        <div className={styles.processingState}>
          <Spinner size={24} />
          <FormattedMessage {...dentalConsultationMessages.reportRecording} />
        </div>
      </div>
    );
  }

  if (isProcessing || isGenerating) {
    return (
      <div className={cn(styles.container, 'p2', className)}>
        <div className={styles.processingState}>
          <Spinner size={24} />
          <FormattedMessage {...dentalConsultationMessages.reportProcessing} />
        </div>
      </div>
    );
  }

  if (isFailed && onRegenerate) {
    return (
      <div className={cn(styles.container, 'p2', className)}>
        <div className={styles.failedState}>
          <FormattedMessage {...dentalConsultationMessages.reportFailed} />
          <Button variant="secondary" size="small" onClick={onRegenerate}>
            {formatMessage(dentalConsultationMessages.regenerateButton)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.container, 'p2', className)}>
      <FormattedMessage {...noDataMessage} />
    </div>
  );
};
